/*
 * Sentry init config, externalised from the inline <head> block to cut parser cost.
 * Liquid-driven values are passed via window.__SENTRY_CFG__ (set inline in theme.liquid).
 * Loaded with `defer` BEFORE the Sentry loader script (also `defer`) so window.sentryOnLoad
 * is always defined before the loader invokes it.
 */
(function () {
  var cfg = window.__SENTRY_CFG__ || {};

  window.sentryOnLoad = function () {
    Sentry.init({
      dsn: "https://5d860ecd70b29896b04de3209ed9a5e1@o4511341161938944.ingest.us.sentry.io/4511341227737088",
      environment: cfg.environment,
      release: cfg.release,
      // Tracing off since 2026-10-28: 5% sampling burned ~450k spans/day against a 5M/month quota.
      // To re-enable, restore browserTracingIntegration + tracesSampleRate + beforeSendTransaction from git history.
      // Keep the explicit 0: the project's Loader has Performance on, which would otherwise default tracing on.
      tracesSampleRate: 0,
      ignoreErrors: [
        // NALA VISUALLY GUARD in layout/theme.liquid stops the Visually SDK on purpose.
        "Visually is disabled on the storefront",
        // --- Network failures (user's connection) ---
        "Load failed",                          // WebKit / Safari / iOS phrasing
        "Failed to fetch",                      // Chrome / Firefox phrasing
        "Failed to load",
        "Failed to read",
        "Access is denied",
        "URIError",
        "URI malformed",
        "NetworkError when attempting to fetch resource", // Firefox
        "The network connection was lost",      // iOS
        "The Internet connection appears to be offline",  // iOS
        "cancelled",                            // iOS request cancellation
        "Network request failed",
        "TypeError: cancelled",
        "AbortError",                           // user navigated away mid-request

        // --- Browser extensions injecting code (not your bug) ---
        /extension\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        /^safari-web-extension:\/\//i,

        // --- Generic noise that's almost never actionable ---
        "Non-Error promise rejection captured",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Script error.",                        // cross-origin script with no details
        "is not a function",

        // --- iOS in-app browser quirks ---
        "InvalidStateError",                    // common in webviews
        "AbortError: The operation was aborted",

        // --- Third-party scripts misbehaving ---
        "fb_xd_fragment",                       // ancient Facebook SDK leftover
        "Can't find variable: _AutofillCallbackHandler",  // iOS Safari autofill
        "Cannot redefine property: ethereum",   // crypto wallet extensions
        "Cannot redefine property: solana",
        "instantSearchSDKJSBridgeClearHighlight", // Bing in-app browser injection
        "Shadow root cannot be created on a host which already hosts a shadow tree", // Clarity clarity.js attachShadow
        // Shopify web-pixel worker (/cdn/wpm/) failing to load — 324k events in the Oct 1–3 2026 bot storm ($82 PAYG)
        /importScripts.*WorkerGlobalScope/i,
        "webkit.messageHandlers",               // iOS in-app browser bridge
        "Receiving end does not exist",         // browser extension messaging

        // --- Issues ignored in the Sentry UI (ignored issues still bill — drop them here) ---
        "Java object is gone",                  // Android in-app browser bridge (iabjs://)
        "Java exception was raised",            // same bridge, different failure
        "keys: data, sentMessage",              // non-Error rejection from an in-app browser bridge
        "webPixelsManager",                     // Shopify web-pixel worker
        "NetworkError: A network error occurred",
        /(^|: )Network Error$/,                 // axios-style network failure on /account
        "Importing a module script failed",     // Safari module-load failure, gives no URL
        /dynamically imported module: https:\/\/(wearnala\.com\/a\/elevar\/|cdn\.shopify\.com\/shopifycloud\/)/, // Elevar config / Shop Pay chunk
        /evaluating '[a-z]\[[a-z]\(\d+\)\]=function/, // obfuscated third-party script

        // --- Unfixable on our side (browser / in-app webview / third-party, no stack of ours) ---
        /IDB(Database|ObjectStore)/,            // IndexedDB in FB/IG webviews, native frames only
        "Can't find variable: indexedDB",       // Safari lockdown / private mode
        "Event `ProgressEvent`",                // Klaviyo profile XHR failing (captured rejection)
        "Event `XMLHttpRequestProgressEvent`",  // same, Safari phrasing
        "Event `CustomEvent` (type=unhandledrejection)",
        "condition rejected",                   // third-party wait-for helper, 0 users
        "Importing binding name",               // Safari stale module cache
        "The element has no supported sources", // media.play() on webviews without the codec
        "The operation is not supported",       // same, Google app on iOS
        /load error: https:\/\/cdn\.shopify\.com\//, // Shop Pay / shop-js loader
      ],
      denyUrls: [
        /assets\.visually\.io/,                 // Visually SDK (disabled on purpose)
        /\/extensions\/[0-9a-f-]{36}\//,        // Shopify app extensions
        /\/cdn\/wpm\//,                         // Shopify web-pixel manager
        /googletagmanager\.com|\/gtm\.js/,      // GTM tags
        /\/widget-assets\/widget-my-rewards\//, // Yotpo loyalty widget
        /\/public\/launcher\.js/,               // third-party launcher (Unauthorized on engaged session)
        /\/lib\/replay\.js/,                    // third-party session replay
        /\/we\/[0-9a-f]+\/common\.js/,          // third-party postMessage helper
      ],
      // Headless browsers / crawlers that slip past Sentry's web-crawler inbound filter.
      beforeSend: function (event) {
        if (/HeadlessChrome|Lighthouse|bot\b|crawler|spider/i.test(navigator.userAgent)) {
          return null;
        }
        return event;
      },
    });
    Sentry.setTag("template", cfg.template);
    Sentry.setTag("shop", cfg.shop);

    // Internal tester identification
    // Usage: append ?nala_test=<your_name> to any URL (e.g. ?nala_test=thao)
    // The tag persists across navigation within the same browser session.
    try {
      var params = new URLSearchParams(window.location.search);
      var testerFromUrl = params.get("nala_test");
      if (testerFromUrl) {
        sessionStorage.setItem("nala_test", testerFromUrl);
      }
      var tester = sessionStorage.getItem("nala_test");
      if (tester) {
        Sentry.setTag("nala_tester", tester);
        Sentry.setTag("internal_test", "true");
      }
    } catch (e) {
      // sessionStorage blocked — fall back to URL-only tagging
      var fallbackParams = new URLSearchParams(window.location.search);
      var fallbackTester = fallbackParams.get("nala_test");
      if (fallbackTester) {
        Sentry.setTag("nala_tester", fallbackTester);
        Sentry.setTag("internal_test", "true");
      }
    }

    // Identify the visitor
    if (cfg.customer) {
      Sentry.setUser({
        id: cfg.customer.id,
        email: cfg.customer.email,
        username: cfg.customer.username,
      });
    } else {
      try {
        var anonId = localStorage.getItem("nala_visitor_id");
        if (!anonId) {
          anonId = "anon-" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
          localStorage.setItem("nala_visitor_id", anonId);
        }
        Sentry.setUser({ id: anonId });
      } catch (e) {
        Sentry.setUser({ id: "anon-" + Math.random().toString(36).substring(2, 15) });
      }
    }
  };
})();
